import { useMemo } from 'react';
import { createTheme } from '@mui/material/styles';
import { zhTW } from '@mui/material/locale';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores';

// Font family presets for different languages
// Using system font stack (insforge style) for native look and zero network requests
const FONT_FAMILIES = {
  // English font stack - system UI fonts
  en: [
    'ui-sans-serif',
    'system-ui',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
    '"Noto Color Emoji"',
  ].join(','),
  // Chinese font stack - prioritize Microsoft JhengHei, then system fonts
  zh: [
    '"Microsoft JhengHei"',
    '"微軟正黑體"',
    'ui-sans-serif',
    'system-ui',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
    '"Noto Color Emoji"',
  ].join(','),
};

export const useTheme = () => {
  const { i18n } = useTranslation();
  const themeMode = useAppStore((state) => state.ui.themeMode);

  const theme = useMemo(() => {
    // Get the current language (en or zh)
    const currentLanguage = i18n.language.startsWith('en') ? 'en' : 'zh';

    // Determine actual mode (handle 'system' preference)
    const isDark = themeMode === 'dark';

    return createTheme(
      {
        palette: {
          mode: isDark ? 'dark' : 'light',
          primary: {
            main: '#1976d2',
            light: '#42a5f5',
            dark: '#1565c0',
          },
          secondary: {
            main: '#dc004e',
          },
          background: isDark
            ? {
                default: '#121212',
                paper: '#1e1e1e',
              }
            : {
                default: '#f5f5f5',
                paper: '#ffffff',
              },
        },
        typography: {
          // Dynamically set fontFamily based on current language
          fontFamily: FONT_FAMILIES[currentLanguage as keyof typeof FONT_FAMILIES],
          fontSize: 13.6,
          htmlFontSize: 16,
          fontWeightRegular: 600,
          body1: {
            fontSize: '0.85rem',
            fontWeight: 600,
          },
          body2: {
            fontSize: '0.85rem',
            fontWeight: 600,
          },
          caption: {
            fontSize: '0.85rem',
            fontWeight: 600,
          },
          subtitle1: {
            fontSize: '1rem',
            fontWeight: 600,
          },
          subtitle2: {
            fontSize: '0.85rem',
            fontWeight: 600,
          },
          button: {
            fontSize: '0.85rem',
            fontWeight: 600,
          },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                scrollbarWidth: 'thin',
                fontSize: '0.85rem',
                fontWeight: 600,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#c1c1c1',
                  borderRadius: '4px',
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                fontSize: '0.85rem',
                fontWeight: 600,
                minHeight: '2.25rem',
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiInputBase-input': {
                  fontSize: '0.85rem',
                  fontWeight: 600,
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.85rem',
                  fontWeight: 600,
                },
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              select: {
                fontSize: '0.85rem',
                fontWeight: 600,
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                fontSize: '0.85rem',
                fontWeight: 600,
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                fontSize: '0.85rem',
                fontWeight: 600,
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontSize: '0.85rem',
                fontWeight: 600,
              },
            },
          },
        },
      },
      zhTW
    );
  }, [i18n.language, themeMode]);

  return theme;
};
